// src/components/RappelPaiements.jsx
// Petit guide flottant en bas à droite (style « Guide de démarrage » de Stripe),
// affiché sur toutes les pages de l'espace créateur tant que les paiements
// ne sont pas activés. Disparaît tout seul une fois le compte Stripe actif.
//
// À placer UNE fois dans le layout : EspaceClient.jsx → <RappelPaiements />

import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check, ChevronDown, ChevronUp, X, Wallet } from "lucide-react";
import { getStatutStripe, demarrerOnboardingStripe } from "../services/StripeConnectService";

const CLE_MASQUE = "thrundrz_rappel_paiements_masque_jusqua";
const DUREE_MASQUE_H = 24; // la croix cache le rappel 24 h, puis il revient

const lireMasque = () => {
    try {
        const jusqua = Number(localStorage.getItem(CLE_MASQUE) || 0);
        return Date.now() < jusqua;
    } catch {
        return false;
    }
};

const RappelPaiements = () => {
    const { pathname } = useLocation();
    const [statut, setStatut] = useState(null);
    const [ouvert, setOuvert] = useState(false);
    const [masque, setMasque] = useState(lireMasque);
    const [redirection, setRedirection] = useState(false);

    // On recharge le statut à chaque changement de page (il a pu changer)
    useEffect(() => {
        getStatutStripe()
            .then((s) => setStatut(s))
            .catch(() => setStatut(null));
    }, [pathname]);

    // Pas de rappel : statut inconnu, compte actif, fermé, ou pendant le retour Stripe
    if (!statut || statut.statut === "actif" || masque || pathname.includes("/stripe/")) {
        return null;
    }

    const etapes = [
        { label: "Créer votre espace créateur", fait: true },
        { label: "Connecter votre compte bancaire", fait: !!statut.details_submitted },
        { label: "Validation par Stripe", fait: statut.statut === "actif" },
    ];
    const faites = etapes.filter((e) => e.fait).length;
    const progression = Math.round((faites / etapes.length) * 100);
    const prochaine = etapes.find((e) => !e.fait);

    const texteBouton = {
        non_connecte: "Activer mes paiements",
        incomplet: "Reprendre la configuration",
        action_requise: "Compléter mes informations",
    }[statut.statut];

    const fermer = () => {
        try {
            localStorage.setItem(CLE_MASQUE, String(Date.now() + DUREE_MASQUE_H * 3600 * 1000));
        } catch { /* stockage indisponible : on masque juste pour cette visite */ }
        setMasque(true);
    };

    const activer = async () => {
        setRedirection(true);
        try {
            await demarrerOnboardingStripe(pathname);
        } catch {
            setRedirection(false);
        }
    };

    return (
        <div className="fixed bottom-4 right-4 z-50 w-[calc(100vw-2rem)] max-w-xs font-Poppins">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                {/* En-tête */}
                <div className="flex items-center gap-2 px-4 pt-3.5">
                    <Wallet size={16} className="text-violet-600 shrink-0" />
                    <h3 className="flex-1 text-sm font-bold text-gray-900">Activez vos paiements</h3>
                    <button
                        onClick={() => setOuvert((o) => !o)}
                        title={ouvert ? "Réduire" : "Voir les étapes"}
                        className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
                    >
                        {ouvert ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                    <button onClick={fermer} title="Me le rappeler demain" className="p-1 rounded-md text-gray-500 hover:bg-gray-100">
                        <X size={16} />
                    </button>
                </div>

                {/* Barre de progression */}
                <div className="px-4 mt-2.5">
                    <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                            className="h-full rounded-full bg-violet-600 transition-all duration-500"
                            style={{ width: `${progression}%` }}
                        />
                    </div>
                </div>

                {/* Détail des étapes */}
                {ouvert && (
                    <ul className="px-4 mt-3 flex flex-col gap-2">
                        {etapes.map((e) => (
                            <li key={e.label} className="flex items-center gap-2 text-xs">
                                <span
                                    className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${e.fait ? "bg-violet-600 text-white" : "border border-gray-300"}`}
                                >
                                    {e.fait && <Check size={10} strokeWidth={3} />}
                                </span>
                                <span className={e.fait ? "text-gray-400 line-through" : "text-gray-700"}>{e.label}</span>
                            </li>
                        ))}
                        <li className="text-[11px] text-gray-500 leading-snug mt-1">
                            Sans paiements activés, vos billets payants ne peuvent pas être vendus.
                        </li>
                    </ul>
                )}

                {/* Étape suivante + bouton */}
                <div className="px-4 py-3">
                    {statut.statut === "en_verification" ? (
                        <p className="text-xs text-gray-600">
                            Étape suivante : <span className="font-semibold text-gray-800">Stripe vérifie vos informations</span>
                        </p>
                    ) : (
                        <>
                            <p className="text-xs text-gray-600 truncate">
                                Étape suivante : <span className="font-semibold text-violet-600">{prochaine?.label}</span>
                            </p>
                            <button
                                onClick={activer}
                                disabled={redirection}
                                className="mt-2.5 w-full py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold transition disabled:opacity-60"
                            >
                                {redirection ? "Redirection…" : texteBouton}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RappelPaiements;